import React, { useState } from 'react';
import { IcClose, IcLink, IcExternalLink, IcCalendar, IcTag, IcBanknote, IcCheckCircle, IcAlertCircle } from '../../theme/icons';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import clientRecordService from '../../services/clientRecordService';
import QrScanner from '../../components/QrScanner';

const fmtMoney = (a) =>
  `${new Intl.NumberFormat('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(Number(a) || 0)} DT`;
const fmtDate = (d) => { try { return format(new Date(d), 'PPP', { locale: fr }); } catch (_) { return d || '-'; } };

// Normalise a scanned/typed value into an openable URL.
export const toUrl = (v) => {
  const s = (v || '').trim();
  if (!s) return '';
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

// Détail of one purchased service. The only thing we attach is a LINK (typed or
// scanned from a QR). If a link exists you open it; otherwise you add one.
const ServiceRecordDetailModal = ({ row, customerId, category, onClose, onSaved }) => {
  const rec = row.record || null;
  const [link, setLink] = useState(rec?.link || rec?.qr_data || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm';

  const open = () => { if (link) window.open(toUrl(link), '_blank', 'noopener'); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('customer', customerId);
      fd.append('sale_service', row.sale_service_id);
      fd.append('category', category);
      fd.append('type', row.service_name || '');
      fd.append('link', link || '');
      fd.append('qr_data', link || '');
      await clientRecordService.saveRecord(fd, rec?.id);
      onSaved();
    } catch (_) {
      setError("Échec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop z-[60] flex items-center justify-center p-4" onMouseDown={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head px-5 py-4">
          <h3 className="modal-title truncate">{row.service_name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose /></button>
        </div>

        {/* Vente info (read-only) */}
        <div className="px-5 py-3 grid grid-cols-2 gap-2 text-sm border-b border-gray-100">
          <div className="flex items-center gap-2 text-gray-600"><IcCalendar className="text-blue-500" /> {fmtDate(row.date)}</div>
          <div className="flex items-center gap-2 text-gray-600"><IcTag className="text-blue-500" /> {row.service_type || '—'}</div>
          <div className="flex items-center gap-2 text-gray-600"><IcBanknote className="text-blue-500" /> {fmtMoney(row.montant)}</div>
          <div>
            {row.paid
              ? <span className="bdg bdg-pos"><IcCheckCircle size={12} /> Payé</span>
              : <span className="bdg bdg-critical"><IcAlertCircle size={12} /> Non payé</span>}
          </div>
        </div>

        <form onSubmit={save} className="px-5 py-4 space-y-3">
          {/* If a link already exists, offer to open it */}
          {rec?.link && (
            <button type="button" onClick={open}
              className="btn-s w-full text-sm px-4 py-2.5">
              <IcExternalLink /> Ouvrir le lien
            </button>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1"><IcLink className="text-[10px]" /> Lien</label>
            <input type="text" value={link} onChange={(e) => setLink(e.target.value)} className={inputCls} placeholder="https://…  (ou scanner un QR)" />
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">…ou scanner un QR code</p>
            <QrScanner onDetected={(val) => setLink(val)} />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-g px-4 py-2 text-sm">Fermer</button>
            <button type="submit" disabled={saving} className="btn-p px-4 py-2 text-sm">
              {saving ? 'Enregistrement…' : 'Enregistrer le lien'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceRecordDetailModal;
